import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";
import { processHomeCPR, transformCPRData } from "../../../../api/cprServices";
import { getHomeCoverPrices } from "../../../../api/quoteServices";
import redflag from "../../../../assets/images/bahrainsmlogo.svg";
import erroricon from "../../../../assets/images/error.svg";
import useHomeFlow from "../../../../hooks/useHomeFlowSimple";
import useHomeFormData from "../../../../hooks/useHomeFormData";
import useLanguageTranslate from "../../../../hooks/useLanguageTranslate";
import BottomStickyBtnLayout from "../../../../pages/motorInsurancePages/newMotorPolicy/Layouts/BottomStickyBtnLayout";
import SignInModal from "../../../../pages/motorInsurancePages/newMotorPolicy/planPages/signInModal";
import { selectHomeFormData } from "../../../../redux/features/homeFormSlice";
import { selectLoginTokenInfo } from "../../../../redux/features/loginRegisterSlice";
import {
  fetchHomeQuote,
  fetchPartyCode,
  selectHomeQuote,
  selectHomeQuoteLoading,
  selectPartyCode,
} from "../../../../redux/features/quoteSlice";
import { formatPremiumValue } from "../../../../utils";
import {
  daysOptions,
  monthsOptions,
  yearsOptions,
} from "../../../../utils/calenderOptions";
import Input from "../../../common/Input/Input";
import FileUploadInput from "../../../common/newFileUploadInput/FileUploadInput";
import SelectInput from "../../../common/selectInput/selectInput";
import SingleDatePicker from "../../../common/SingleDatePicker/SingleDatePicker";
import DomesticHelperModal from "../../../newHomePolicy/modals/domesticHelperModal";
import HomeSummaryModal from "../../../newHomePolicy/modals/homeSummaryModal";
import TitleDescription from "../../../newMotorPolicy/titleDescription/titleDescription";
import NewHomeJourneyLayout from "../../layouts/NewHomeJourneyLayout";

// Constants
const DOMESTIC_HELPER_DEFAULT_SUM_INSURED = "3,000";

const Covers = () => {
  const translate = useLanguageTranslate();
  const dispatch = useDispatch();
  const { handleNextSubstep, handlePrevSubstep } = useHomeFlow();
  const { formData: savedFormData, saveHomeDataInRedux } =
    useHomeFormData("covers");
  const { i18n } = useTranslation();
  const isRTL = i18n.resolvedLanguage === "ar";

  // Helper function to translate cover names
  const translateCoverName = (coverName) => {
    const coverTranslations = {
      "All Risk": translate("All Risk", "جميع المخاطر"),
      "VIP": translate("VIP", "تغطية كبار الشخصيات"),
      "Domestic Helper": translate("Domestic Helper", "العامل المنزلي"),
      "Terrorism": translate("Terrorism", "تغطية التخريب والإرهاب"),
      "Sabotage and Terrorism": translate("Sabotage and Terrorism", "تغطية التخريب والإرهاب"),
    };

    return coverTranslations[coverName] || coverName;
  };

  // Check login status
  const loginTokenInfo = useSelector(selectLoginTokenInfo);
  const isLoggedIn = !!loginTokenInfo;

  // Get party code for logged-in users
  const partyCode = useSelector(selectPartyCode);

  // Get all home form data for premium calculation
  const allHomeFormData = useSelector(selectHomeFormData);

  // Get home quote data from Redux (base premium from previous step)
  const homeQuoteData = useSelector(selectHomeQuote);
  const homeQuoteLoading = useSelector(selectHomeQuoteLoading);

  // Form state
  const [formData, setFormData] = useState({
    allRisk: false,
    allRiskValue: "",
    domesticHelper: false,
    domesticHelperFullName: "",
    domesticHelperDateOfBirth: {
      day: "",
      month: "",
      year: "",
      fullDate: "",
    },
    domesticHelperSumInsured: DOMESTIC_HELPER_DEFAULT_SUM_INSURED,
    cprUploaded: false,
    cprFiles: [],
    cprProcessing: false,
    cprData: null,
    cprProcessed: false,
    vipCover: false,
    sabotageAndTerrorism: false,
  });

  // Cover prices state
  const [coverPrices, setCoverPrices] = useState([]);
  const [coverPricesLoading, setCoverPricesLoading] = useState(false);
  const [coverPricesError, setCoverPricesError] = useState(null);

  // Error state
  const [errors, setErrors] = useState({});

  // Modal state
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [showDomesticHelperModal, setShowDomesticHelperModal] = useState(false);
  const [showHomeSummaryModal, setShowHomeSummaryModal] = useState(false);

  // Ref to track if user was logged in when component first mounted
  const wasLoggedInOnMount = useRef(isLoggedIn);

  // Calculate total premium - static from API until covers are supported
  const calculateTotalPremium = () => {
    // Return base premium from API without adding cover premiums
    // Covers are not yet supported by the API
    if (homeQuoteData) {
      return homeQuoteData.totalPremium || 0;
    }
  };

  // Track if user was logged in when covers page first loaded
  // Only set flag if they were ALREADY logged in on mount (not if they log in from modal)
  useEffect(() => {
    const flagKey = 'home_covers_logged_in_on_load';

    // Only set the flag if:
    // 1. User was logged in when component mounted (wasLoggedInOnMount.current === true)
    // 2. We now have a valid party code (not FALCON)
    // 3. Flag hasn't been set yet
    const flagAlreadySet = sessionStorage.getItem(flagKey);

    if (!flagAlreadySet && wasLoggedInOnMount.current && partyCode && partyCode !== "FALCON") {
      console.log("User was already logged in on mount with party code - marking flag to skip refetch");
      sessionStorage.setItem(flagKey, 'true');
    }

    // Cleanup: Clear the flag when component unmounts (user navigates away)
    return () => {
      sessionStorage.removeItem(flagKey);
    };
  }, [partyCode]); // Only depend on partyCode changes

  // Fetch party code for logged-in users
  useEffect(() => {
    if (isLoggedIn && !partyCode && loginTokenInfo?.cpr) {
      console.log(
        "Home Policy Covers - Fetching party code for logged-in user:",
        loginTokenInfo.cpr
      );
      dispatch(fetchPartyCode(loginTokenInfo.cpr));
    }
  }, [isLoggedIn, partyCode, loginTokenInfo?.cpr, dispatch]);

  // CRITICAL: Re-fetch quote when party code becomes available for logged-in user
  // This handles the case where user was guest, filled forms, then logged in
  // SKIP refetch if user was already logged in when they reached this page
  useEffect(() => {
    const shouldRefetchQuote = async () => {
      // Only re-fetch if:
      // 1. User is logged in
      // 2. We have a party code now (just became available)
      // 3. We already have quote data (that was fetched with FALCON)
      // 4. We have all necessary home form data
      if (
        !isLoggedIn ||
        !partyCode ||
        !homeQuoteData ||
        !allHomeFormData?.homeDetails ||
        !allHomeFormData?.addressDetails
      ) {
        return;
      }

      console.log(
        "=== Home Policy Covers - Checking if quote needs refetch ==="
      );
      console.log("Is logged in:", isLoggedIn);
      console.log("Party code available:", partyCode);
      console.log("Has quote data:", !!homeQuoteData);
      console.log("Has home details:", !!allHomeFormData?.homeDetails);
      console.log("Has address details:", !!allHomeFormData?.addressDetails);

      // NEW: Skip refetch if user was already logged in when they first reached this page
      // This means they logged in BEFORE starting the flow, so the quote was already fetched with their party code
      const wasLoggedInOnLoad = sessionStorage.getItem('home_covers_logged_in_on_load');
      if (wasLoggedInOnLoad === 'true') {
        console.log("User was already logged in when covers page loaded - skipping refetch");
        return;
      }

      // Check if we need to refetch - this prevents infinite loops
      // We refetch once when partyCode becomes available
      const hasRefetchedKey = `home_refetched_${partyCode}`;
      if (sessionStorage.getItem(hasRefetchedKey)) {
        console.log("Already refetched quote with this party code, skipping");
        return;
      }

      console.log("🔄 Refetching home quote with user party code:", partyCode);

      try {
        // Prepare complete home data
        const completeHomeData = {
          ...allHomeFormData,
          covers: formData, // Include current cover selections
        };

        // Dispatch home quote with force refresh
        await dispatch(
          fetchHomeQuote({
            homeFormData: completeHomeData,
            options: { forceRefresh: true },
          })
        );

        // Mark as refetched to prevent duplicate calls
        sessionStorage.setItem(hasRefetchedKey, "true");

        console.log("✅ Home quote refetched with user party code");
      } catch (error) {
        console.error("❌ Error refetching quote with party code:", error);
      }
    };

    shouldRefetchQuote();
  }, [
    partyCode,
    isLoggedIn,
    homeQuoteData,
    allHomeFormData,
    formData,
    dispatch,
  ]);

  // Initialize form with saved data - run only once on mount
  useEffect(() => {
    if (savedFormData && Object.keys(savedFormData).length > 0) {
      setFormData((prevFormData) => ({
        ...prevFormData, // Preserve any other form data
        allRisk: savedFormData.allRisk || false,
        allRiskValue: savedFormData.allRiskValue || "",
        domesticHelper: savedFormData.domesticHelper || false,
        domesticHelperFullName: savedFormData.domesticHelperFullName || "",
        domesticHelperDateOfBirth:
          savedFormData.domesticHelperDateOfBirth ||
          prevFormData.domesticHelperDateOfBirth,
        domesticHelperSumInsured:
          savedFormData.domesticHelperSumInsured ||
          DOMESTIC_HELPER_DEFAULT_SUM_INSURED,
        cprUploaded: savedFormData.cprUploaded || false,
        cprFiles: savedFormData.cprFiles || [],
        cprProcessing: savedFormData.cprProcessing || false,
        cprData: savedFormData.cprData || null,
        cprProcessed: savedFormData.cprProcessed || false,
        vipCover: savedFormData.vipCover || false,
        sabotageAndTerrorism: savedFormData.sabotageAndTerrorism || false,
        // Also restore newer cover code format if available
        cpro21: savedFormData.cpro21 || false,
        cpro13: savedFormData.cpro13 || false,
        cpro35: savedFormData.cpro35 || false,
        cpro33: savedFormData.cpro33 || false,
      }));
    }
  }, [savedFormData]); // Add savedFormData as dependency to react to changes

  // Fetch cover prices when home details are available
  useEffect(() => {
    const fetchCoverPrices = async () => {
      // Only fetch if we have home details data
      if (!allHomeFormData?.homeDetails) {
        console.log("Skipping cover prices fetch - no home details available");
        return;
      }

      setCoverPricesLoading(true);
      setCoverPricesError(null);

      try {
        console.log("=== Fetching Home Cover Prices ===");
        console.log("Home form data:", allHomeFormData);

        const result = await getHomeCoverPrices(allHomeFormData, {
          timeout: 15000,
        });

        if (result.success) {
          console.log("=== Home Cover Prices Success ===");
          console.log("Covers received:", result.covers.length);
          setCoverPrices(result.covers);
        } else {
          console.error("=== Home Cover Prices Failed ===");
          console.error("Error:", result.error);
          setCoverPricesError(result.error);
          toast.error(
            translate(
              "Failed to load cover prices. Default pricing will be used.",
              "فشل في تحميل أسعار التغطية. سيتم استخدام التسعيرة الافتراضية."
            )
          );
        }
      } catch (error) {
        console.error("Cover prices fetch error:", error);
        setCoverPricesError(error.message);
        toast.error(
          translate(
            "Error loading cover prices. Default pricing will be used.",
            "خطأ في تحميل أسعار التغطية. سيتم استخدام التسعيرة الافتراضية."
          )
        );
      } finally {
        setCoverPricesLoading(false);
      }
    };

    // Fetch cover prices when component loads and home details are available
    fetchCoverPrices();
  }, [allHomeFormData?.homeDetails]); // Depend on home details changes

  // Debounced quote update when ALL domestic helper fields are filled and validated
  useEffect(() => {
    // Only trigger if domestic helper is enabled
    const isDomesticHelperEnabled = formData.domesticHelper || formData.cpro35;
    if (!isDomesticHelperEnabled) {
      return;
    }

    // Check if we have full name filled
    const hasFullName =
      formData.domesticHelperFullName &&
      formData.domesticHelperFullName.trim() !== "";

    // Check if we have complete date of birth
    const hasCompleteDOB =
      formData.domesticHelperDateOfBirth?.day &&
      formData.domesticHelperDateOfBirth?.month &&
      formData.domesticHelperDateOfBirth?.year;

    // Check if CPR is uploaded
    const hasCPRUploaded =
      formData.cprUploaded && formData.cprFiles && formData.cprFiles.length > 0;

    // CRITICAL: Only proceed if ALL fields are complete
    if (!hasFullName || !hasCompleteDOB || !hasCPRUploaded) {
      console.log("⏸️ Domestic helper fields not ready for quote update");
      console.log("- Full Name filled:", hasFullName);
      console.log("- Date of Birth complete:", hasCompleteDOB);
      console.log("- CPR uploaded:", hasCPRUploaded);
      return;
    }

    console.log(
      "✅ All domestic helper fields filled, validating before quote update..."
    );
    console.log("Full Name:", formData.domesticHelperFullName);
    console.log(
      "Date of Birth:",
      `${formData.domesticHelperDateOfBirth.day}/${formData.domesticHelperDateOfBirth.month}/${formData.domesticHelperDateOfBirth.year}`
    );
    console.log("CPR Files:", formData.cprFiles.length);

    // Validate Full Name - must contain only alphabetic characters (including Arabic), spaces, and periods
    const nameRegex = /^[a-zA-Z\u0600-\u06FF\s.]+$/;
    if (!nameRegex.test(formData.domesticHelperFullName)) {
      console.log("❌ Full name validation failed: invalid characters");
      setErrors((prev) => ({
        ...prev,
        domesticHelperFullName: translate(
          "Name can only contain alphabetic characters, spaces, and periods.",
          "يجب أن يحتوي الاسم على حروف أبجدية ومسافات ونقاط فقط."
        ),
      }));
      return;
    }

    // Clear full name error if validation passes
    setErrors((prev) => ({
      ...prev,
      domesticHelperFullName: "",
    }));

    // Validate DOB BEFORE triggering quote calculation
    const birthDate = new Date(
      parseInt(formData.domesticHelperDateOfBirth.year),
      parseInt(formData.domesticHelperDateOfBirth.month) - 1,
      parseInt(formData.domesticHelperDateOfBirth.day)
    );
    const today = new Date();

    // Check if date is in the future
    if (birthDate > today) {
      console.log("❌ DOB validation failed: date is in the future");
      setErrors((prev) => ({
        ...prev,
        domesticHelperDateOfBirth: translate(
          "Date of birth cannot be in the future.",
          "لا يمكن أن يكون تاريخ الميلاد في المستقبل."
        ),
      }));
      return;
    }

    // Check if helper is at least 18 years old
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }

    if (age < 18) {
      console.log("❌ DOB validation failed: helper is under 18");
      setErrors((prev) => ({
        ...prev,
        domesticHelperDateOfBirth: translate(
          "Helper must be at least 18 years old.",
          "يجب أن يكون عمر العامل 18 سنة على الأقل."
        ),
      }));
      return;
    }

    // Clear DOB error if validation passes
    setErrors((prev) => ({
      ...prev,
      domesticHelperDateOfBirth: "",
    }));

    // All validations passed - Debounce the API call - wait 800ms after user stops typing/selecting
    console.log("✅ All validations passed, scheduling quote update...");
    const timeoutId = setTimeout(() => {
      console.log("🚀 Triggering quote update with domestic helper data");
      updateQuoteWithNewCovers(formData, true); // true = include domestic helper data
    }, 800);

    // Cleanup timeout on unmount or when dependencies change
    return () => {
      clearTimeout(timeoutId);
    };
  }, [
    formData.domesticHelper,
    formData.cpro35,
    formData.domesticHelperFullName,
    formData.domesticHelperDateOfBirth?.day,
    formData.domesticHelperDateOfBirth?.month,
    formData.domesticHelperDateOfBirth?.year,
    formData.cprUploaded,
    formData.cprFiles?.length,
  ]); // Trigger when domestic helper fields change

  // Helper function to get cover price by cover code
  const getCoverPriceByCode = (coverCode) => {
    if (!coverPrices || !Array.isArray(coverPrices)) return 0;

    const cover = coverPrices.find((c) => c.CoverCode === coverCode);
    const price = cover?.Price || 0;
    console.log(
      `💰 Home Cover ${coverCode}: Found price ${price} for cover:`,
      cover
    );
    return price;
  };

  // Helper functions for specific covers
  const getAllRiskCoverPrice = () => getCoverPriceByCode("CPRO21");
  const getVIPCoverPrice = () => getCoverPriceByCode("CPRO13");
  const getDomesticHelperCoverPrice = () => getCoverPriceByCode("CPRO35");
  const getTerrorismCoverPrice = () => getCoverPriceByCode("CPRO33");

  // Function to update quote when covers change
  const updateQuoteWithNewCovers = async (
    newFormData,
    includeDomesticHelperData = false
  ) => {
    try {
      console.log("=== Updating Home Quote with New Covers ===");
      console.log("New cover selections:", newFormData);
      console.log("Include domestic helper data:", includeDomesticHelperData);

      // CRITICAL: For logged-in users, wait for partyCode to be fetched
      if (isLoggedIn && !partyCode) {
        console.log("⏳ Waiting for party code before updating quote...");
        // Trigger fetch if not already fetching
        if (loginTokenInfo?.cpr) {
          dispatch(fetchPartyCode(loginTokenInfo.cpr));
        }
        toast.info(
          translate(
            "Loading account information. Please wait...",
            "جاري تحميل معلومات الحساب. يرجى الانتظار..."
          )
        );
        return;
      }

      console.log("Using party code for quote update:", partyCode || "FALCON");

      // Prepare complete home data with new cover selections
      let completeHomeData = {
        ...allHomeFormData,
        covers: newFormData,
      };

      // Add domestic helper specific data if requested
      if (
        includeDomesticHelperData &&
        (newFormData.domesticHelper || newFormData.cpro35)
      ) {
        completeHomeData = {
          ...completeHomeData,
          domesticHelperCount: 1,
          domesticHelperLimit: 3000,
          domesticMembers: [
            {
              name: newFormData.domesticHelperFullName || "",
              cprOrPassport: "", // Leave empty for now as per requirement
            },
          ],
        };
        console.log("📋 Added domestic helper data to quote:", {
          domesticHelperCount: 1,
          domesticHelperLimit: 3000,
          domesticMembers: completeHomeData.domesticMembers,
        });
      }

      // Dispatch home quote with force refresh since covers changed
      await dispatch(
        fetchHomeQuote({
          homeFormData: completeHomeData,
          options: { forceRefresh: true }, // Force new API call for cover changes
        })
      );

      console.log("✅ Home quote updated with new covers");
    } catch (error) {
      console.error("❌ Error updating quote with covers:", error);
      toast.error(
        translate(
          "Error updating quote. Please try again.",
          "خطأ في تحديث التسعيرة. يرجى المحاولة مرة أخرى."
        )
      );
    }
  };

  const handleSwitchChange = (switchName, value) => {
    // Check if this is Domestic Helper being enabled
    const isDomesticHelperBeingEnabled =
      (switchName === "cpro35" || switchName === "domesticHelper") && value === true;

    setFormData((prev) => {
      const newFormData = {
        ...prev,
        [switchName]: value,
      };

      // Save cover selections to Redux immediately for persistence
      setTimeout(() => {
        const formDataWithPrices = {
          ...newFormData,
          coverPrices: coverPrices,
        };
        saveHomeDataInRedux(formDataWithPrices);
      }, 100);

      // Update quote with new cover selection (debounced to avoid multiple calls)
      // SKIP the quote call for Domestic Helper when being ENABLED
      // The useEffect will handle the quote call once all fields (CPR, name, DOB) are filled and validated
      if (!isDomesticHelperBeingEnabled) {
        setTimeout(() => {
          updateQuoteWithNewCovers(newFormData);
        }, 300);
      } else {
        console.log("⏸️ Domestic Helper enabled - waiting for all fields to be filled before quote call");
      }

      return newFormData;
    });

    // Clear errors when switching off
    if (!value) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        // Handle Domestic Helper (CPRO35) specific validations
        if (switchName === "cpro35" || switchName === "domesticHelper") {
          delete newErrors.domesticHelperFullName;
          delete newErrors.domesticHelperDateOfBirth;
          delete newErrors.domesticHelperSumInsured;
          delete newErrors.cprUpload;
        }
        return newErrors;
      });
    }
  };

  const handleDateChange = (field, value) => {
    setFormData((prev) => {
      const currentDOB = prev.domesticHelperDateOfBirth || {
        day: "",
        month: "",
        year: "",
        fullDate: "",
      };

      const newDOB = {
        ...currentDOB,
        [field]: value,
      };

      if (newDOB.day && newDOB.month && newDOB.year) {
        newDOB.fullDate = `${newDOB.year}-${newDOB.month.padStart(
          2,
          "0"
        )}-${newDOB.day.padStart(2, "0")}`;
      } else {
        newDOB.fullDate = "";
      }

      return {
        ...prev,
        domesticHelperDateOfBirth: newDOB,
      };
    });

    // Immediate DOB validation once all 3 fields are filled
    const updatedDOB = { ...formData.domesticHelperDateOfBirth, [field]: value };
    if (updatedDOB.day && updatedDOB.month && updatedDOB.year) {
      const birthDate = new Date(
        parseInt(updatedDOB.year),
        parseInt(updatedDOB.month) - 1,
        parseInt(updatedDOB.day)
      );
      const today = new Date();
      if (birthDate > today) {
        setErrors((prev) => ({
          ...prev,
          domesticHelperDateOfBirth: translate(
            "Date of birth cannot be in the future.",
            "لا يمكن أن يكون تاريخ الميلاد في المستقبل."
          ),
        }));
      } else {
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
          age--;
        }
        if (age < 18) {
          setErrors((prev) => ({
            ...prev,
            domesticHelperDateOfBirth: translate(
              "Helper must be at least 18 years old.",
              "يجب أن يكون عمر العامل 18 سنة على الأقل."
            ),
          }));
        } else {
          setErrors((prev) => ({ ...prev, domesticHelperDateOfBirth: "" }));
        }
      }
    } else {
      // Not all parts filled yet — clear any stale error
      if (errors.domesticHelperDateOfBirth) {
        setErrors((prev) => ({ ...prev, domesticHelperDateOfBirth: "" }));
      }
    }
  };

  // Handle date picker selection (for SingleDatePicker component)
  const handleDatePickerSelect = (selectedDate) => {
    if (selectedDate) {
      const date = new Date(selectedDate);
      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear().toString();

      setFormData((prev) => ({
        ...prev,
        domesticHelperDateOfBirth: {
          day,
          month,
          year,
          fullDate: selectedDate,
        },
      }));

      // Immediate DOB validation
      const today = new Date();
      const birthDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      if (birthDate > today) {
        setErrors((prev) => ({
          ...prev,
          domesticHelperDateOfBirth: translate(
            "Date of birth cannot be in the future.",
            "لا يمكن أن يكون تاريخ الميلاد في المستقبل."
          ),
        }));
      } else {
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
          age--;
        }
        if (age < 18) {
          setErrors((prev) => ({
            ...prev,
            domesticHelperDateOfBirth: translate(
              "Helper must be at least 18 years old.",
              "يجب أن يكون عمر العامل 18 سنة على الأقل."
            ),
          }));
        } else {
          setErrors((prev) => ({ ...prev, domesticHelperDateOfBirth: "" }));
        }
      }
    }
  };


  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Immediate inline validation for full name
    if (field === "domesticHelperFullName") {
      if (!value.trim()) {
        setErrors((prev) => ({
          ...prev,
          domesticHelperFullName: translate(
            "This field is required.",
            "هذا الحقل مطلوب"
          ),
        }));
      } else if (!/^[a-zA-Z\u0600-\u06FF\s.]+$/.test(value)) {
        setErrors((prev) => ({
          ...prev,
          domesticHelperFullName: translate(
            "Name can only contain alphabetic characters, spaces, and periods.",
            "يجب أن يحتوي الاسم على حروف أبجدية ومسافات ونقاط فقط."
          ),
        }));
      } else {
        setErrors((prev) => ({ ...prev, domesticHelperFullName: "" }));
      }
      return;
    }

    // Clear error when user starts typing for other fields
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  // CPR file upload handler - following travel insurance pattern
  const handleFileSelect = async (fileData) => {
    // Strict validation: Only allow maximum 2 files for CPR (front and back)
    const currentFiles = formData.cprFiles || [];
    if (currentFiles.length >= 2) {
      toast.error(
        translate(
          "Maximum 2 files allowed (CPR front and back)",
          "يُسمح بحد أقصى ملفين (الوجه الأمامي والخلفي للهوية)"
        )
      );
      return;
    }

    // Update form with uploaded file
    setFormData((prev) => {
      const currentFiles = prev.cprFiles || [];
      // Double check to prevent race conditions
      if (currentFiles.length >= 2) {
        return prev; // Don't update if already at limit
      }
      return {
        ...prev,
        cprFiles: [...currentFiles, fileData],
        cprUploaded: true,
        cprProcessing: true, // Start processing state
      };
    });

    // Clear any field errors when files are uploaded
    setErrors((prevErrors) => ({
      ...prevErrors,
      cprUpload: "",
    }));

    // Process CPR with Vista OCR in background
    if (fileData && fileData.file) {
      try {
        // Determine if this is front or back based on current file count
        const currentFileCount = formData.cprFiles?.length || 0;
        const side = currentFileCount === 0 ? "front" : "back";
        console.log(`Starting CPR processing for domestic helper (${side})`);

        const result = await processHomeCPR(fileData.file, side);

        if (result.success && result.data?.output?.status) {
          // CPR processing successful
          console.log("CPR processing completed successfully", result.data);

          const cprData = transformCPRData(result.data);

          // Auto-fill form data with CPR information
          setFormData((prev) => ({
            ...prev,
            // Auto-fill fields from CPR data
            domesticHelperFullName:
              cprData.fullName || prev.domesticHelperFullName,
            domesticHelperDateOfBirth:
              cprData.dateOfBirth &&
              cprData.dateOfBirth.day &&
              cprData.dateOfBirth.month &&
              cprData.dateOfBirth.year
                ? {
                    day: cprData.dateOfBirth.day,
                    month: cprData.dateOfBirth.month,
                    year: cprData.dateOfBirth.year,
                    fullDate: `${
                      cprData.dateOfBirth.year
                    }-${cprData.dateOfBirth.month.padStart(
                      2,
                      "0"
                    )}-${cprData.dateOfBirth.day.padStart(2, "0")}`,
                  }
                : prev.domesticHelperDateOfBirth,
            // Update processing state
            cprProcessing: false,
            cprData: cprData,
            cprProcessed: true,
          }));

          // Show success message
          toast.success(
            translate(
              "CPR processed successfully! Helper information has been auto-filled.",
              "تم معالجة الهوية بنجاح! تم ملء معلومات العامل تلقائياً."
            )
          );
        } else {
          // CPR processing failed
          console.warn(
            "CPR processing failed or returned invalid data",
            result
          );

          setFormData((prev) => ({
            ...prev,
            cprProcessing: false,
          }));

          toast.error(
            translate(
              "Failed to process CPR. Please fill the information manually.",
              "فشل في معالجة الهوية. يرجى ملء المعلومات يدوياً."
            )
          );
        }
      } catch (error) {
        console.error("CPR processing error:", error);

        setFormData((prev) => ({
          ...prev,
          cprProcessing: false,
        }));

        toast.error(
          translate(
            "Error processing CPR. Please try again.",
            "خطأ في معالجة الهوية. يرجى المحاولة مرة أخرى."
          )
        );
      }
    }
  };

  const handleFileRemove = () => {
    setFormData((prev) => ({
      ...prev,
      cprFiles: [],
      cprUploaded: false,
      cprProcessing: false,
      cprData: null,
      cprProcessed: false,
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    // No validation needed for All Risk as it no longer has input fields

    // Validate Domestic Helper fields if enabled (dynamic cover key support)
    const domesticHelperEnabled = formData.domesticHelper || formData.cpro35;
    if (domesticHelperEnabled) {
      if (
        !formData.domesticHelperFullName ||
        formData.domesticHelperFullName.trim() === ""
      ) {
        newErrors.domesticHelperFullName = translate(
          "This field is required.",
          "هذا الحقل مطلوب"
        );
      } else if (!/^[a-zA-Z\u0600-\u06FF\s.]+$/.test(formData.domesticHelperFullName)) {
        newErrors.domesticHelperFullName = translate(
          "Name can only contain alphabetic characters, spaces, and periods.",
          "يجب أن يحتوي الاسم على حروف أبجدية ومسافات ونقاط فقط."
        );
      }

      if (
        !formData.domesticHelperDateOfBirth?.day ||
        !formData.domesticHelperDateOfBirth?.month ||
        !formData.domesticHelperDateOfBirth?.year
      ) {
        newErrors.domesticHelperDateOfBirth = translate(
          "This field is required.",
          "هذا الحقل مطلوب"
        );
      } else {
        // Validate that helper is at least 18 years old
        const birthDate = new Date(
          parseInt(formData.domesticHelperDateOfBirth.year),
          parseInt(formData.domesticHelperDateOfBirth.month) - 1,
          parseInt(formData.domesticHelperDateOfBirth.day)
        );
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();

        if (
          monthDiff < 0 ||
          (monthDiff === 0 && today.getDate() < birthDate.getDate())
        ) {
          age--;
        }

        if (age < 18) {
          newErrors.domesticHelperDateOfBirth = translate(
            "Helper must be at least 18 years old.",
            "يجب أن يكون عمر العامل 18 سنة على الأقل."
          );
        }

        if (birthDate > today) {
          newErrors.domesticHelperDateOfBirth = translate(
            "Date of birth cannot be in the future.",
            "لا يمكن أن يكون تاريخ الميلاد في المستقبل."
          );
        }
      }

      // Validate CPR upload is required and has correct number of files
      if (
        !formData.cprUploaded ||
        !formData.cprFiles ||
        formData.cprFiles.length === 0
      ) {
        newErrors.cprUpload = translate(
          "This field is required.",
          "هذا الحقل مطلوب."
        );
      } else if (formData.cprFiles.length > 2) {
        newErrors.cprUpload = translate(
          "Maximum 2 files allowed (CPR front and back).",
          "يُسمح بحد أقصى ملفين (الوجه الأمامي والخلفي للهوية)."
        );
      }

      // Skip validation for domesticHelperSumInsured as it's a fixed value
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleContinue = () => {
    // Save form data to Redux with cover prices for other components
    const formDataWithPrices = {
      ...formData,
      coverPrices: coverPrices, // Save cover prices for use in summary
      coverPriceHelpers: {
        getCoverPriceByCode,
        getAllRiskCoverPrice,
        getVIPCoverPrice,
        getDomesticHelperCoverPrice,
        getTerrorismCoverPrice,
      },
    };
    saveHomeDataInRedux(formDataWithPrices);

    // Validate form before proceeding
    if (validateForm()) {
      // Check if user is logged in before proceeding
      if (!isLoggedIn) {
        setShowSignInModal(true);
        return;
      }

      handleNextSubstep();
    }
  };

  const handleSignInSuccess = () => {
    setShowSignInModal(false);
    // After successful login, automatically proceed to next step
    handleNextSubstep();
  };

  return (
    <div className="w-full">
      {/* SignIn Modal */}
      <SignInModal
        show={showSignInModal}
        setShow={setShowSignInModal}
        cbSuccess={handleSignInSuccess}
      />

      <NewHomeJourneyLayout
        is_hide_back_btn={false}
        customBackHandler={handlePrevSubstep}
      >
        <BottomStickyBtnLayout
          btn_onClick={handleContinue}
          btn_label={
            homeQuoteLoading
              ? translate("Updating Quote...", "جاري تحديث التسعيرة...")
              : translate("Continue", "التالي")
          }
          btn_disabled={formData.cprProcessing || homeQuoteLoading}
          show_summary={homeQuoteLoading ? true : false}
          price={homeQuoteLoading ? 0 : calculateTotalPremium()}
          passedFrom="HomeInsurance"
          isLoading={homeQuoteLoading}
          quoteData={homeQuoteData}
          addonComponent={
            <div className="mr-10 mt-3 max767:w-full max767:flex max767:mb-2 max767:justify-between ipad:mt-0 rtl:ml-10 rtl:mr-0">
              {!homeQuoteLoading && (
                <button
                  onClick={() => setShowHomeSummaryModal(true)}
                  className="text-[#00384D] text-center text-base not-italic font-semibold underline mr-10 rtl:ml-10 rtl:mr-0"
                >
                  {translate("Show Summary", "عرض التسعيرة")}
                </button>
              )}
              {!homeQuoteLoading && (
                <strong className="text-[#1A2024] text-xl font-extrabold">
                  {(() => {
                    const totalPremium = calculateTotalPremium();
                    const formattedPrice = formatPremiumValue(
                      totalPremium,
                      "en-US",
                      3
                    );
                    return isRTL
                      ? `${formattedPrice} د.ب`
                      : `BHD ${formattedPrice}`;
                  })()}
                </strong>
              )}
            </div>
          }
        >
          <div className="flex justify-center items-start min-h-[60vh]">
            <div className="w-full max-w-[600px] mx-auto px-4 pb-28 max767:pb-10">
              <TitleDescription
                title={translate("Enhance Your Coverage", "تغطيات إضافية")}
                description={translate(
                  "Would you like to add extra covers and benefits to your policy?",
                  "هل ترغب بإضافة تغطيات وفوائد إضافية إلى وثيقتك؟"
                )}
              />

              <div className="space-y-2 mt-8">
                {/* Dynamic Covers based on API response */}
                {coverPrices.map((cover) => {
                  const coverKey = cover.CoverCode.toLowerCase();
                  const isActive = formData[coverKey] || false;

                  // Special handling for Domestic Helper with info icon and fields
                  const isDomesticHelper =
                    cover.CoverName === "Domestic Helper" ||
                    cover.CoverCode === "CPRO35";

                  // Special handling for All Risk with additional input field
                  const isAllRisk =
                    cover.CoverName === "All Risk" ||
                    cover.CoverCode === "CPRO21";

                  return (
                    <div
                      key={cover.CoverCode}
                      className={`bg-white rounded-lg border-2 p-4 transition-all duration-300 ${
                        isActive ? "border-[#00719A]" : "border-[#DDE2E4]"
                      }`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center">
                          <span className="text-base font-normal rtl:font-medium text-[#252C32] rtl:text-[#48535B] mr-2 rtl:mr-0 rtl:ml-2">
                            {translateCoverName(cover.CoverName)}
                          </span>
                          {/* Only show info icon for Domestic Helper */}
                          {isDomesticHelper && (
                            <button
                              onClick={() => setShowDomesticHelperModal(true)}
                              className="w-4 h-4 flex items-center justify-center hover:opacity-70 transition-opacity"
                            >
                              <img
                                src={erroricon}
                                alt="info"
                                className="w-4 h-4 mx-1"
                              />
                            </button>
                          )}
                        </div>
                        <label
                          className={`relative inline-flex items-center ${
                            homeQuoteLoading
                              ? "cursor-not-allowed opacity-50"
                              : "cursor-pointer"
                          }`}
                        >
                          <input
                            type="checkbox"
                            className="sr-only"
                            checked={isActive}
                            disabled={homeQuoteLoading}
                            onChange={(e) =>
                              !homeQuoteLoading &&
                              handleSwitchChange(coverKey, e.target.checked)
                            }
                          />
                          <div
                            className={`w-8 h-5 rounded-full transition-colors ${
                              isActive ? "bg-[#00A9E7]" : "bg-gray-300"
                            }`}
                          >
                            <div
                              className={`size-4 bg-white rounded-full shadow transform transition-transform mt-0.5 ${
                                isActive
                                  ? isRTL
                                    ? "-translate-x-3.5"
                                    : "translate-x-3.5"
                                  : isRTL
                                  ? "-translate-x-[0.15rem]"
                                  : "translate-x-[0.15rem]"
                              }`}
                            ></div>
                          </div>
                        </label>
                      </div>

                      {/* Show price badge when inactive or active */}
                      {!isActive && (
                        <div className="flex justify-end">
                          <span className="bg-[#EEF0F2] text-[#252C32] px-2 py-0.5 rounded-full text-xs font-medium uppercase">
                            {isRTL
                              ? `+${cover.Price} د.ب`
                              : `+ BHD ${cover.Price}`}
                          </span>
                        </div>
                      )}

                      {/* All Risk specific fields */}
                      {isAllRisk && isActive && (
                        <div className="mt-4 space-y-4">
                          <div className="flex justify-end">
                            <span className="bg-[#00719A] text-white px-2 py-0.5 rounded-full text-xs font-medium uppercase">
                              {isRTL
                                ? `+${cover.Price} د.ب`
                                : `+ BHD ${cover.Price}`}
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Domestic Helper specific fields */}
                      {isDomesticHelper && isActive && (
                        <div className="mt-4 space-y-4">
                          {/* CPR Upload */}
                          <div>
                            <label className="label text-[#48535B] text-base not-italic font-normal leading-[26px] tracking-[-0.16px]">
                              {translate(
                                "Upload CPR (front & back)",
                                "قم بتحميل CPR (الوجه والظهر)"
                              )}
                            </label>
                            <FileUploadInput
                              name="domestic_helper_cpr"
                              onFileSelect={handleFileSelect}
                              onFileRemove={handleFileRemove}
                              validTypes={[
                                "image/jpeg",
                                "image/png",
                                "application/pdf",
                              ]}
                              maxSizeKB={5000}
                              uploadToServer={true}
                              multiple={true}
                              maxFiles={2}
                              disabled={
                                formData.cprProcessing || homeQuoteLoading
                              }
                              initialFiles={formData.cprFiles || []}
                            />
                            {errors.cprUpload && (
                              <div className="mt-2 text-red-600 text-sm">
                                {errors.cprUpload}
                              </div>
                            )}

                            {/* CPR Processing Status */}
                            {formData.cprProcessing && (
                              <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                <div className="flex items-center space-x-2 rtl:space-x-reverse">
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                                  <p className="text-sm text-blue-700">
                                    {translate(
                                      "Processing CPR data...",
                                      "جاري معالجة بيانات الهوية..."
                                    )}
                                  </p>
                                </div>
                                <p className="text-xs text-blue-600 mt-1">
                                  {translate(
                                    "Please wait while we extract your information...",
                                    "يرجى الانتظار بينما نستخرج معلوماتك..."
                                  )}
                                </p>
                              </div>
                            )}
                          </div>

                          {/* Sum Insured Value */}
                          <div>
                            <label className="label text-[#48535B] text-base not-italic font-normal leading-[26px] tracking-[-0.16px]">
                              {translate(
                                "Sum Insured value",
                                "قيمة مبلغ التأمين"
                              )}
                            </label>
                            <Input
                              className="px-4 py-6 max767:items-center max767:w-full text-2xl not-italic font-normal"
                              endAdornment={
                                <div className="flex align-middle mt-1">
                                  <img src={redflag} alt="flag" />
                                  <p className="ml-2 text-black text-lg not-italic font-normal leading-[26px] tracking-[-0.18px] rtl:mr-2 rtl:ml-0">
                                    {translate("BHD", "د.ب")}
                                  </p>
                                </div>
                              }
                              type="text"
                              formatNumber={true}
                              name="domesticHelperSumInsured"
                              value={formData.domesticHelperSumInsured}
                              handleChange={(e) =>
                                handleInputChange(
                                  "domesticHelperSumInsured",
                                  e.target.value
                                )
                              }
                              error={errors.domesticHelperSumInsured}
                              disabled={true}
                            />
                          </div>

                          {/* Full Name */}
                          <div>
                            <label className="label text-[#48535B] text-base not-italic font-normal leading-[26px] tracking-[-0.16px]">
                              {translate("Full Name", "اسم العامل المنزلي")}
                            </label>
                            <Input
                              type="text"
                              name="domesticHelperFullName"
                              value={formData.domesticHelperFullName}
                              handleChange={(e) =>
                                handleInputChange(
                                  "domesticHelperFullName",
                                  e.target.value
                                )
                              }
                              error={errors.domesticHelperFullName}
                              disabled={
                                formData.cprProcessing || homeQuoteLoading
                              }
                            />
                          </div>

                          {/* Date of Birth */}
                          <div>
                            <label className="label text-[#48535B] text-base not-italic font-normal leading-[26px] tracking-[-0.16px] mb-[5px] block">
                              {translate("Date of Birth", "تاريخ الولادة")}
                            </label>

                            {/* Mobile: native date picker (visible <=767px) */}
                            <div className="hidden max767:block">
                              <SingleDatePicker
                                value={
                                  formData.domesticHelperDateOfBirth
                                    ?.fullDate || ""
                                }
                                onChange={handleDatePickerSelect}
                                placeholder={translate(
                                  "Select Date",
                                  "اختر التاريخ"
                                )}
                                disabled={
                                  formData.cprProcessing || homeQuoteLoading
                                }
                                error={errors.domesticHelperDateOfBirth}
                                useNativeMobile={true}
                              />
                            </div>
                            {/* Desktop: select dropdowns (visible >767px) */}
                            <div className="block max767:hidden">
                              <div className="flex gap-6 self-stretch">
                                <SelectInput
                                  name="day"
                                  options_data={daysOptions}
                                  formData={{
                                    day:
                                      formData.domesticHelperDateOfBirth?.day ||
                                      "",
                                  }}
                                  default_placeholder={translate("Day", "يوم")}
                                  handleChange={(e) =>
                                    handleDateChange("day", e.target.value)
                                  }
                                  disabled={
                                    formData.cprProcessing || homeQuoteLoading
                                  }
                                  className="text-black"
                                  maxMenuHeight={185}
                                />

                                <SelectInput
                                  name="month"
                                  options_data={monthsOptions}
                                  formData={{
                                    month:
                                      formData.domesticHelperDateOfBirth
                                        ?.month || "",
                                  }}
                                  default_placeholder={translate(
                                    "Month",
                                    "شهر"
                                  )}
                                  handleChange={(e) =>
                                    handleDateChange("month", e.target.value)
                                  }
                                  disabled={
                                    formData.cprProcessing || homeQuoteLoading
                                  }
                                  maxMenuHeight={185}
                                />

                                <SelectInput
                                  name="year"
                                  options_data={yearsOptions}
                                  formData={{
                                    year:
                                      formData.domesticHelperDateOfBirth
                                        ?.year || "",
                                  }}
                                  default_placeholder={translate("Year", "سنة")}
                                  handleChange={(e) =>
                                    handleDateChange("year", e.target.value)
                                  }
                                  disabled={
                                    formData.cprProcessing || homeQuoteLoading
                                  }
                                  maxMenuHeight={185}
                                />
                              </div>
                              {errors.domesticHelperDateOfBirth && (
                                <div className="mt-2 text-red-600 text-sm">
                                  {errors.domesticHelperDateOfBirth}
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="flex justify-end">
                            <span className="bg-[#00719A] text-white px-2 py-0.5 rounded-full text-xs font-medium uppercase">
                              {isRTL
                                ? `+${cover.Price} د.ب`
                                : `+ BHD ${cover.Price}`}
                            </span>
                          </div>
                        </div>
                      )}

                      {/* General active price badge for non-special covers */}
                      {isActive && !isAllRisk && !isDomesticHelper && (
                        <div className="flex justify-end">
                          <span className="bg-[#00719A] text-white px-2 py-0.5 rounded-full text-xs font-medium uppercase">
                            {isRTL
                              ? `+${cover.Price} د.ب`
                              : `+ BHD ${cover.Price}`}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Show loading state */}
                {coverPricesLoading && (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="text-sm text-gray-600 mt-2">
                      {translate("Loading covers...", "جاري تحميل التغطيات...")}
                    </p>
                  </div>
                )}

                {/* Show error state */}
                {coverPricesError && coverPrices.length === 0 && (
                  <div className="text-center py-8">
                    <p className="text-sm text-red-600">
                      {translate(
                        "Failed to load covers. Please refresh the page.",
                        "فشل في تحميل التغطيات. يرجى تحديث الصفحة."
                      )}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </BottomStickyBtnLayout>
      </NewHomeJourneyLayout>

      {/* Domestic Helper Modal */}
      <DomesticHelperModal
        show={showDomesticHelperModal}
        onClose={() => setShowDomesticHelperModal(false)}
      />

      {/* Home Summary Modal */}
      <HomeSummaryModal
        show={showHomeSummaryModal}
        onClose={() => setShowHomeSummaryModal(false)}
        homeFormData={{
          ...allHomeFormData,
          covers: {
            ...formData,
            coverPrices: coverPrices, // Include cover prices for pricing lookup in modal
          },
        }}
        quoteData={homeQuoteData}
      />
    </div>
  );
};

export default Covers;
